<template>
  <div class="task-data" @click.stop="emit('card-click', $event)">
    <TaskHeader
      :session="session"
      :task-name="taskName"
      :start-date="formattedStartDate"
      :duration="session.formatted_duration || duration"
      :user-list="userList"
      :permissions="permissions"
      :show-icons="true"
      :show-logs="showLogs"
      :can-delete="canDelete"
      @click="emit('card-click', $event)"
      @logs="emit('logs')"
      @refresh="emit('refresh')"
      @delete="openTaskActions"
    />

    <hr v-if="showProgress" class="task-data-separator">

    <TaskProgress
      v-if="showProgress"
      class="task-progress-card"
      :status="sessionStatus || statusInfo?.status"
      :percent="statusInfo?.percent || 0"
      :message="statusInfo?.message || message"
      :stage-label="statusInfo?.stage_label"
      :stage-number="statusInfo?.stage_number"
      :total-stages="statusInfo?.total_stages"
      :task-u-i-d="taskUID"
      :user-name="userName"
      :parent-u-i-d="parentUID"
    />

    <hr class="task-data-separator">

    <TaskBatchStatus
      :process_info="processInfo"
    />
  </div>

  <Modal
    ref="taskActionsModal"
    v-model:show="showTaskActions"
    title="Delete Task"
    width="420px"
    class="task-actions-modal"
  >
    <template #modal-main>
      <div class="task-actions-dialog">
        <p
          class="task-action-warning"
          :class="{
            'task-action-message-success': taskActionMessageType === 'success',
            'task-action-message-error': taskActionMessageType === 'error',
          }"
        >
          {{ taskActionMessage }}
        </p>
        <button
          v-if="taskActionMessageType === 'warning'"
          class="task-action-button"
          @click="handleStopTask"
        >
          Stop Task and kill associated Processes
        </button>
        <button
          v-if="taskActionMessageType === 'warning'"
          class="task-action-button"
          @click="handleStopAndRemoveTask"
        >
          Stop Task and also remove from the view
        </button>
      </div>
    </template>
    <template #modal-footer>
      <button class="task-action-cancel" @click="closeTaskActions">
        Close
      </button>
    </template>
  </Modal>
</template>

<script setup>
import { computed, ref } from 'vue';
import TaskHeader from '@/components/Apps/TaskHeader.vue';
import TaskProgress from '@/components/Apps/TaskProgress.vue';
import TaskBatchStatus from '@/components/Apps/TaskBatchStatus.vue';
import Modal from '@/components/Modal.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import { encodeID } from '@/assets/js/utils.js';

const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
  taskName: {
    type: String,
    default: '',
  },
  startDate: {
    type: String,
    default: '',
  },
  duration: {
    type: [Number, String],
    default: '',
  },
  userList: {
    type: Array,
    default: () => [],
  },
  permissions: {
    type: Object,
    default: () => ({}),
  },
  showLogs: {
    type: Boolean,
    default: true,
  },
  showProgress: {
    type: Boolean,
    default: true,
  },
  canDelete: {
    type: Boolean,
    default: true,
  },
  sessionStatus: {
    type: String,
    default: '',
  },
  statusInfo: {
    type: Object,
    default: () => ({}),
  },
  message: {
    type: String,
    default: '',
  },
  taskUID: {
    type: String,
    default: '',
  },
  userName: {
    type: String,
    default: '',
  },
  parentUID: {
    type: String,
    default: '',
  },
  processInfo: {
    type: Object,
    default: () => ({
      id: '',
      status: '',
    }),
  },
});

const formattedStartDate = computed(() => {
  if (!props.startDate) {
    return '';
  }

  const date = new Date(props.startDate);
  if (Number.isNaN(date.getTime())) {
    return props.startDate;
  }

  return date.toLocaleTimeString('default', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  });
});

const emit = defineEmits(['card-click', 'logs', 'stop', 'refresh', 'terminate-session', 'delete']);
const showTaskActions = ref(false);
const taskActionsModal = ref(null);
const taskActionMessage = ref('');
const taskActionMessageType = ref('warning');
const shouldRefreshAfterTaskAction = ref(false);
const shouldDeleteTask = ref(false);
const dataConnect = new DatastoreConnect();

const openTaskActions = () => {
  taskActionMessage.value = `Delete task ${encodeID(props.session.id, 'bt-')}`;
  taskActionMessageType.value = 'warning';
  showTaskActions.value = true;
};

const closeTaskActions = () => {
  taskActionsModal.value?.closeModal();
  showTaskActions.value = false;
  if (shouldRefreshAfterTaskAction.value) {
    shouldRefreshAfterTaskAction.value = false;
    if (shouldDeleteTask.value) {
      shouldDeleteTask.value = false;
      emit('delete', props.session);
    } else {
      emit('terminate-session', props.session);
    }
  }
};

const handleTaskAction = async (deleteAlso = false) => {
  try {
    const response = await dataConnect.api('job.stop', {
      task_id: props.session.id,
      ...(deleteAlso ? { delete_also: true } : {}),
    });
    if (response?.result) {
      taskActionMessage.value = response.result.message || response.message || 'Task stopped successfully.';
      taskActionMessageType.value = 'success';
      shouldRefreshAfterTaskAction.value = true;
      shouldDeleteTask.value = deleteAlso;
      return;
    }
    taskActionMessage.value = response?.error?.message || 'Failed to terminate task';
    taskActionMessageType.value = 'error';
  } catch (error) {
    taskActionMessage.value = error?.message || 'Failed to terminate task';
    taskActionMessageType.value = 'error';
  }
};

const handleStopTask = () => handleTaskAction();
const handleStopAndRemoveTask = () => handleTaskAction(true);
</script>

<style scoped lang="scss">
.task-data {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  padding: 4px;
  box-sizing: border-box;
}

.task-progress-card {
  width: 100%;
}

.task-data-separator {
  width: 100%;
  border: 0;
  border-top: 1px solid #e5e7eb;
  margin: 0;
}

.task-actions-dialog {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 20px;
}

.task-action-warning {
  margin: 0 0 4px;
  color: var(--body-text-color, #222);
  text-align: center;
}

.task-action-message-success { color: #166534; }
.task-action-message-error { color: #b91c1c; }

.task-action-button {
  width: 100%;
  min-height: 44px;
  padding: 10px 14px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  background: #e5e7eb;
  color: #1f2937;
  cursor: pointer;
}

.task-action-button:hover {
  border-color: #dc2626;
  background: #dc2626;
  color: #fff;
}

.task-action-cancel {
  display: block;
  min-width: 90px;
  margin: 12px auto 20px;
  padding: 7px 18px;
  border: 1px solid #9ca3af;
  border-radius: 4px;
  background: #e5e7eb;
  color: #1f2937;
  cursor: pointer;
}

.task-action-cancel:hover {
  border-color: #166534;
  background: #166534;
  color: #fff;
}
</style>
